import { Box, Icon, LabelTooltip, Position, Tooltip, theme, Stat, Gap } from '@salecycle/ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useReportableDataState } from '../../context/UseReportableDataContext';
import {
  calculateSalesPercentageChange,
  formatComparisonCurrency,
  formatComparisonRate,
  formatRate,
} from '../../utils/formatting';
import { localiseCurrency } from '../../utils/localization';

const NorthStarPanelElement = styled.div`
  background: linear-gradient(180deg, #026eea 0%, #7d02fd 100%);
  color: white;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative;
`;

interface ITooltipWrapperProps {
  margin: string;
}

const TooltipWrapper = styled.span<ITooltipWrapperProps>`
  margin: ${({ margin }) => margin};
`;

export const renderTooltip = (
  tooltipHeading: string,
  tooltipDescription: string,
  margin = '0 0 0 5px',
  position: Position = 'right',
): JSX.Element => (
  <TooltipWrapper margin={margin}>
    <Tooltip
      html={<LabelTooltip heading={tooltipHeading} description={tooltipDescription} />}
      preventOverflow={false}
      position={position}
    >
      <Icon icon="question-circle" size="small" />
    </Tooltip>
  </TooltipWrapper>
);

export default function NorthStarPanel() {
  const { t } = useTranslation('dashboard');
  const [showContent, setShowContent] = useState(false);
  const [salesContribution, setSalesContribution] = useState(0);
  const [comparisonSalesContribution, setComparisonSalesContribution] = useState(0);
  const {
    data: { salesTotals, comparisonSalesTotals },
    graphParameters: { baseCurrency, comparisonEnabled },
    loading,
  } = useReportableDataState();

  // If the data has been initialised (i.e. has had data loaded at least once)
  // then we no longer show the placeholders.
  useEffect(() => {
    const salesTotalsExists = salesTotals !== undefined;
    setShowContent(!loading && salesTotalsExists);
  }, [loading, salesTotals]);

  useEffect(() => {
    const salesTotalsExists = salesTotals !== undefined;
    setShowContent(!loading && salesTotalsExists);
  }, [loading, salesTotals]);

  useEffect(() => {
    setSalesContribution(calculateSalesPercentageChange(salesTotals.DirectValue, salesTotals.RecoveredValue));
    setComparisonSalesContribution(
      calculateSalesPercentageChange(comparisonSalesTotals.DirectValue, comparisonSalesTotals.RecoveredValue),
    );
  }, [salesTotals, comparisonSalesTotals]);

  return (
    <NorthStarPanelElement data-testid="NorthStarPanel">
      <Box stackItems={true} margin="35px 0">
        <Stat
          variation="panel"
          mode="dark"
          value={localiseCurrency(salesTotals.RecoveredValue, baseCurrency)}
          name={t('metrics.recoveredRevenue.heading')}
          icon="usd-circle"
          iconColour={theme.vars.newColor.white}
          titlePosition="bottom"
          showPlaceholder={!showContent}
          tooltipHeading={t('metrics.recoveredRevenue.tooltipHeading')}
          tooltipDescription={t('metrics.recoveredRevenue.tooltipDescription')}
          comparisonValue={
            comparisonEnabled ? formatComparisonCurrency(comparisonSalesTotals.RecoveredValue, baseCurrency, t) : null
          }
        />

        {/* {!showContent && <Gap top={40} />}

        <Stat
          variation="panel"
          mode="dark"
          icon="star"
          name={t('metrics.conversionRate.heading')}
          value={formatRate(northStarTotals.v1ConversionRate.value)}
          iconColour={theme.vars.newColor.white}
          titlePosition="bottom"
          showPlaceholder={!showContent}
          tooltipHeading={t('metrics.conversionRate.tooltipHeading')}
          tooltipDescription={t('metrics.conversionRate.tooltipDescription')}
          comparisonValue={
            comparisonEnabled
              ? formatComparisonRate(comparisonEnabled, northStarTotals.v1ConversionRate.comparison, dateRange, t)
              : null
          }
        /> */}
        {!showContent && <Gap top={40} />}
        <Stat
          variation="panel"
          mode="dark"
          icon="usd-circle"
          name={t('ratesPanel.salesContribution')}
          value={formatRate(salesContribution, true)}
          iconColour={theme.vars.newColor.white}
          titlePosition="bottom"
          showPlaceholder={!showContent}
          tooltipHeading={t('ratesPanel.salesContribution')}
          tooltipDescription={t('ratesPanel.salesContributionTooltipDescription')}
          comparisonValue={comparisonEnabled ? formatComparisonRate(comparisonSalesContribution, t, true) : null}
        />
      </Box>
    </NorthStarPanelElement>
  );
}
